import { ProjectGridField } from '@/types/fields'
import { Badge, Card, List, ListSubheader, useTheme } from '@mui/material'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import FieldListItem from '@/components/project-settings/FieldListItem'

type FieldInfo = {
  field: ProjectGridField
  fields?: ProjectGridField[]
}

type FieldRulesFieldListProps = {
  fieldInfos: FieldInfo[]
  selectedField: ProjectGridField
  setSelectedField: Dispatch<SetStateAction<ProjectGridField | null>>
}

type FieldRulesFieldListItemProps = {
  field: ProjectGridField
  selectedField: ProjectGridField
  onSelectField: (field: ProjectGridField) => void
  indented?: boolean
}

function FieldRulesFieldListItem({
  field,
  selectedField,
  onSelectField,
  indented,
}: FieldRulesFieldListItemProps) {
  return (
    <FieldListItem
      field={field}
      isButton
      indented={indented}
      selected={selectedField.id === field.id}
      onSelectField={() => onSelectField(field)}
    >
      {(field.project_grid_field_rules?.length || 0) > 0 && (
        <Badge
          badgeContent={field?.project_grid_field_rules?.length}
          color="neutral"
        />
      )}
    </FieldListItem>
  )
}

export default function FieldRulesFieldList({
  fieldInfos,
  selectedField,
  setSelectedField,
}: FieldRulesFieldListProps) {
  const theme = useTheme()
  const [currentFieldEl, setCurrentFieldEl] = useState<HTMLDivElement | null>(
    null,
  )
  const skipScrolling = useRef(false)

  const onSelectField = (field: ProjectGridField) => {
    setSelectedField(field)
    skipScrolling.current = true
  }

  useEffect(() => {
    if (!skipScrolling.current) {
      currentFieldEl?.scrollIntoView({ block: 'center' })
    }
  }, [currentFieldEl, selectedField.id])

  return (
    <Card sx={{ overflow: 'auto', height: '100%', position: 'relative' }}>
      <List dense>
        {fieldInfos.map((fieldInfo) => {
          return !fieldInfo.fields ? (
            <div
              key={fieldInfo.field.id}
              ref={
                fieldInfo.field.id === selectedField.id
                  ? (el) => setCurrentFieldEl(el as HTMLImageElement)
                  : null
              }
            >
              <FieldRulesFieldListItem
                field={fieldInfo.field}
                selectedField={selectedField}
                onSelectField={onSelectField}
              />
            </div>
          ) : (
            <List key={fieldInfo.field.id} dense sx={{ my: 1 }}>
              <ListSubheader
                sx={{ background: theme.palette.background.paper, zIndex: 2 }}
              >
                {fieldInfo.field.name}
              </ListSubheader>
              {fieldInfo.fields.map((subField) => {
                return (
                  <div
                    key={subField.id}
                    ref={
                      subField.id === selectedField.id
                        ? (el) => setCurrentFieldEl(el as HTMLImageElement)
                        : null
                    }
                  >
                    <FieldRulesFieldListItem
                      field={subField}
                      selectedField={selectedField}
                      onSelectField={onSelectField}
                      indented
                    />
                  </div>
                )
              })}
            </List>
          )
        })}
      </List>
    </Card>
  )
}
